import { Show, createSignal, type Component, For } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { PanelBadge } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";

type StoreName = "My" | "Root" | "CA" | "TrustedPeople" | "TrustedPublisher";
type StoreLocation = "LocalMachine" | "CurrentUser";

const STORE_NAMES: StoreName[] = ["My", "Root", "CA", "TrustedPeople", "TrustedPublisher"];
const STORE_LOCATIONS: StoreLocation[] = ["LocalMachine", "CurrentUser"];

interface CertRow {
  Subject?: string;
  Thumbprint?: string;
  NotAfter?: string;
}

interface ListResult {
  rows?: CertRow[];
}

interface DetailsResult {
  thumbprint?: string;
  details?: unknown;
}

function isExpired(notAfter: string | undefined): boolean {
  if (!notAfter) return false;
  const t = Date.parse(notAfter);
  if (!Number.isFinite(t)) return false;
  return t < Date.now();
}

function formatDate(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try {
      return new Date(v).toLocaleString();
    } catch {
      return v;
    }
  }
  return String(v);
}

const CertsPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [storeName, setStoreName] = createSignal<StoreName>("My");
  const [storeLocation, setStoreLocation] = createSignal<StoreLocation>("LocalMachine");
  const [filter, setFilter] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [modal, setModal] = createSignal<{ title: string; body: string } | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const listQuery = createToolQuery<ListResult>(() => ({
    tool: "cert_store_list",
    params: { storeName: storeName(), storeLocation: storeLocation() },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  function applyFilter(rows: CertRow[]): CertRow[] {
    const q = filter().trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      for (const k of Object.keys(r)) {
        const v = (r as Record<string, unknown>)[k];
        if (v == null) continue;
        if (String(v).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }

  async function showDetails(thumb: string): Promise<void> {
    if (!thumb) return;
    setActionStatus({ text: "Chargement details...", level: "info" });
    try {
      const r = await tools.call<DetailsResult>("cert_details", {
        storeName: storeName(),
        storeLocation: storeLocation(),
        thumbprint: thumb,
      });
      let body: string;
      try {
        body = JSON.stringify(r?.details ?? r, null, 2);
      } catch {
        body = String(r);
      }
      setModal({ title: `Certificat ${r?.thumbprint || thumb}`, body });
      setActionStatus({ text: "Details charges.", level: "info" });
    } catch (err) {
      setActionStatus({
        text: `Erreur details: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function deleteCert(thumb: string): Promise<void> {
    if (!thumb) return;
    const ok = await confirm.confirm({
      title: "Supprimer certificat",
      body: `Confirmer la suppression du certificat ${thumb} dans ${storeLocation()}\\${storeName()} ?`,
      level: "danger",
      confirmLabel: "Supprimer",
      entityName: thumb,
    });
    if (!ok) return;
    setActionStatus({ text: "Suppression...", level: "info" });
    try {
      await tools.call(
        "cert_action",
        {
          storeName: storeName(),
          storeLocation: storeLocation(),
          thumbprint: thumb,
          action: "delete",
        },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Certificat ${thumb} supprime.`, level: "info" });
      await listQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function exportCert(thumb: string): Promise<void> {
    if (!thumb) return;
    const raw = window.prompt("Chemin d'export (Windows, absolu, extension .cer/.crt/.der):", `C:\\Temp\\${thumb}.cer`);
    if (raw == null) return;
    const exportPath = String(raw).trim();
    if (!/^[A-Za-z]:\\[A-Za-z0-9 _\-.\\()]{1,255}\.(cer|crt|der)$/.test(exportPath) || exportPath.includes("..")) {
      setActionStatus({ text: "Chemin d'export invalide.", level: "danger" });
      return;
    }
    const ok = await confirm.confirm({
      title: "Exporter certificat",
      body: `Exporter ${thumb} vers ${exportPath} ?`,
      level: "warn",
      confirmLabel: "Exporter",
    });
    if (!ok) return;
    setActionStatus({ text: "Export...", level: "info" });
    try {
      await tools.call(
        "cert_action",
        {
          storeName: storeName(),
          storeLocation: storeLocation(),
          thumbprint: thumb,
          action: "export",
          exportPath,
        },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Certificat exporte vers ${exportPath}.`, level: "info" });
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  const columns: Column<CertRow>[] = [
    { accessorKey: "Subject", header: "Subject" },
    { accessorKey: "Thumbprint", header: "Thumbprint" },
    {
      accessorKey: "NotAfter",
      header: "NotAfter",
      cell: (info) => {
        const v = String(info.getValue() ?? "");
        return (
          <>
            <span>{formatDate(v)}</span>
            <Show when={isExpired(v)}>
              <span style={{ "margin-left": "0.5em" }}>
                <PanelBadge intent="danger">expire</PanelBadge>
              </span>
            </Show>
          </>
        );
      },
    },
  ];

  const toolbar = (
    <>
      <input
        type="text"
        class="panel-filter-input"
        placeholder="Filtre (Subject, Thumbprint)..."
        spellcheck={false}
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <select
        class="panel-filter-input panel-filter-input--narrow"
        value={storeLocation()}
        onChange={(e) => setStoreLocation(e.currentTarget.value as StoreLocation)}
      >
        <For each={STORE_LOCATIONS}>{(l) => <option value={l}>{l}</option>}</For>
      </select>
      <select
        class="panel-filter-input panel-filter-input--narrow"
        value={storeName()}
        onChange={(e) => setStoreName(e.currentTarget.value as StoreName)}
      >
        <For each={STORE_NAMES}>{(s) => <option value={s}>{s}</option>}</For>
      </select>
      <button type="button" class="btn btn--xs btn--info" onClick={() => listQuery.refetch()}>
        Rafraichir
      </button>
    </>
  );

  const statusBar = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (listQuery.isFetching) return { text: "Chargement...", level: "info" };
    if (listQuery.error)
      return {
        text: `Erreur: ${listQuery.error instanceof Error ? listQuery.error.message : String(listQuery.error)}`,
        level: "danger",
      };
    return undefined;
  };

  return (
    <>
      <PanelLayout title="Certificats" toolbar={toolbar} status={statusBar()}>
        <DataTable<CertRow>
          rows={applyFilter(listQuery.data?.rows ?? [])}
          columns={columns}
          emptyMessage="Aucun certificat."
          rowActions={(r) => {
            const thumb = String(r.Thumbprint || "");
            return (
              <>
                <button type="button" class="btn btn--xs btn--ghost" onClick={() => showDetails(thumb)}>
                  Details
                </button>
                <button type="button" class="btn btn--xs btn--warn" onClick={() => exportCert(thumb)}>
                  Export
                </button>
                <button type="button" class="btn btn--xs btn--danger" onClick={() => deleteCert(thumb)}>
                  Delete
                </button>
              </>
            );
          }}
        />
      </PanelLayout>
      <Show when={modal()}>
        {(m) => (
          <div
            class="panel-modal-overlay"
            style={{
              position: "fixed",
              inset: "0",
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              "align-items": "center",
              "justify-content": "center",
              "z-index": "9999",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setModal(null);
            }}
          >
            <div
              class="panel-modal"
              style={{
                background: "#1f2128",
                color: "#e7e7e7",
                "max-width": "80vw",
                "max-height": "80vh",
                width: "720px",
                "border-radius": "8px",
                display: "flex",
                "flex-direction": "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "0.75em 1em",
                  "border-bottom": "1px solid #333",
                  display: "flex",
                  "justify-content": "space-between",
                  "align-items": "center",
                }}
              >
                <strong>{m().title}</strong>
                <button type="button" class="btn btn--xs btn--ghost" onClick={() => setModal(null)}>
                  Fermer
                </button>
              </div>
              <pre
                style={{
                  margin: "0",
                  padding: "1em",
                  overflow: "auto",
                  "font-size": "12px",
                  "white-space": "pre-wrap",
                  "word-break": "break-all",
                  "max-height": "60vh",
                }}
              >
                {m().body}
              </pre>
            </div>
          </div>
        )}
      </Show>
    </>
  );
};

registerPanel("certs", CertsPanel);
export default CertsPanel;
